import { defineStore } from "pinia";
import { ref, computed } from "vue";
import axios from "@/plugins/axios";
import { v4 as uuidv4 } from "uuid";
import { showMessage } from "@/utils/notifications";

export const usePersonStore = defineStore("person", () => {
  // State
  const personsList = ref([]);

  // Getters
  const getPersonsList = computed(() => personsList.value);

  // Actions
  const setPersonsList = (data) => {
    personsList.value = data;
  };

  const addPerson = (data) => {
    if (!data.hasOwnProperty("default_email")) {
      data.default_email = data.email[0].email;
    }
    if (!data.hasOwnProperty("investors")) {
      data.investors = [];
    }
    personsList.value.push(data);
  };

  const updateAddedPerson = (data) => {
    let index;
    if (data.tempId) {
      index = personsList.value.findIndex((e) => e.tempId == data.tempId);
    } else {
      index = personsList.value.findIndex((e) => e.entity_id == data.person.entity_id);
    }

    if (data.person) {
      if (!data.person.hasOwnProperty("default_email")) {
        data.person.default_email = personsList.value[index].default_email;
      }
      if (!data.person.hasOwnProperty("investors")) {
        data.person.investors = personsList.value[index].investors;
      }
    }
    personsList.value.splice(index, 1, data.person);
  };

  const deleteAddedPerson = (data) => {
    let index = personsList.value.findIndex((e) => e.tempId == data.tempId);
    personsList.value.splice(index, 1);
  };

  const deleteUser = (payload) => {
    let index = personsList.value.findIndex((e) => e.entity_id == payload.entity_id);
    if (index > -1) {
      personsList.value.splice(index, 1);
    }
  };

  // API Actions
  const getPersons = async () => {
    try {
      const response = await axios.get(`/person`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return resData;
      }

      setPersonsList(resData.data);
      return resData;
    } catch (error) {
      console.error("Error fetching persons:", error);
      return { success: false, message: "Failed to fetch persons" };
    }
  };

  const getPersonsByInvestorId = async (investor_id) => {
    try {
      const response = await axios.get(`/person/investor/${investor_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
      }
      return resData;
    } catch (error) {
      console.error("Error fetching persons by investor:", error);
      return { success: false, message: "Failed to fetch persons by investor" };
    }
  };

  const getPersonsInvestorRelated = async () => {
    try {
      const response = await axios.get(`/person/related`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
      }
      return resData;
    } catch (error) {
      console.error("Error fetching related persons:", error);
      return { success: false, message: "Failed to fetch related persons" };
    }
  };

  const addPersonAction = async (payload) => {
    let person = Object.assign({}, payload);
    if (!person.entity_id) {
      person.tempId = "PERSON_" + uuidv4();
      addPerson(person);
    }

    try {
      const { data } = await axios.post(`/person`, {
        entity_id: payload.entity_id,
        version: payload.version,
        first_name: payload.first_name,
        last_name: payload.last_name,
        is_admin: payload.is_admin,
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        if (person.tempId) {
          deleteAddedPerson({ tempId: person.tempId });
        }
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      updateAddedPerson({ person: data.person, tempId: person.tempId });
      return data.person;
    } catch (error) {
      console.error("Error adding person:", error);
      return { success: false, message: "Failed to add person" };
    }
  };

  const editEmails = async (payload) => {
    try {
      const { data } = await axios.post("/email", {
        emails: payload.emails,
        emails_to_delete: payload.emails_to_delete,
        is_new_user: payload.is_new_user,
        person_id: payload.person_id,
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return true;
    } catch (error) {
      console.error("Error editing emails:", error);
      return { success: false, message: "Failed to edit emails" };
    }
  };

  const invitePerson = async (payload) => {
    try {
      const { data } = await axios.post(`/person/invite`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return true;
    } catch (error) {
      console.error("Error inviting person:", error);
      return { success: false, message: "Failed to invite person" };
    }
  };

  const resendInvite = async (person_id) => {
    try {
      const { data } = await axios.post(`/person/resend-invitation`, { person_id: person_id });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return true;
    } catch (error) {
      console.error("Error resending invite:", error);
      return { success: false, message: "Failed to resend invite" };
    }
  };

  const removePerson = async (payload) => {
    try {
      const { data } = await axios.post(`/person/remove`, payload);

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      return true;
    } catch (error) {
      console.error("Error removing person:", error);
      return { success: false, message: "Failed to remove person" };
    }
  };

  const getEmails = async (payload) => {
    try {
      const response = await axios.get(`/email/${payload.person_id}`);
      const resData = response.data;

      if (!resData.success) {
        showMessage({ ...resData, messageType: "error", messageTitle: "Error" });
        return;
      } else {
        return resData.data;
      }
    } catch (error) {
      console.error("Error fetching emails:", error);
      return [];
    }
  };

  const deleteUserAction = async (payload) => {
    try {
      const { data } = await axios.delete(`/person`, {
        data: { entity_id: payload.entity_id },
      });

      if (!data.success) {
        showMessage({ ...data, messageType: "error", messageTitle: "Error" });
        return data;
      }

      showMessage({ ...data, messageType: "success", messageTitle: "Success" });
      deleteUser(payload);
      return true;
    } catch (error) {
      console.error("Error deleting user:", error);
      return { success: false, message: "Failed to delete user" };
    }
  };

  return {
    // State
    personsList,

    // Getters
    getPersonsList,

    // Actions
    setPersonsList,
    addPerson,
    updateAddedPerson,
    deleteAddedPerson,
    deleteUser,

    // API Actions
    getPersons,
    getPersonsByInvestorId,
    getPersonsInvestorRelated,
    addPersonAction,
    editEmails,
    invitePerson,
    resendInvite,
    removePerson,
    getEmails,
    deleteUserAction,
  };
});
